/**
 * Form-field wrapper shared by the edit-property, edit-apartment, and
 * edit-room wizards: a label, the control, and an optional hint or
 * validation error below it.
 *
 * A hint longer than 140 characters is collapsed behind an (i) icon next to
 * the label instead of printing a paragraph under every field — the same
 * idea as the long Section-header descriptions, one level down.
 */
import { Label } from "@/components/ui/label";
import { InfoTooltip } from "@/components/admin/v2/primitives/InfoTooltip";

const MAX_INLINE_HINT_CHARS = 140;

export function Field({
  label,
  required,
  hint,
  error,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  /** Validation message for this field. Replaces the hint while it is set. */
  error?: string | null;
  className?: string;
  children: React.ReactNode;
}) {
  const isLongHint = !!hint && hint.length > MAX_INLINE_HINT_CHARS;

  return (
    <div className={`space-y-1.5 ${className ?? ""}`}>
      <div className="flex items-center gap-1">
        {/* A <label> must not contain interactive descendants, so the info
            control sits beside it rather than inside it. */}
        <Label className="flex items-center gap-1 text-xs font-medium">
          {label}
          {required && <span className="text-destructive">*</span>}
        </Label>
        {!error && isLongHint && <InfoTooltip description={hint} />}
      </div>
      {children}
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : (
        hint &&
        !isLongHint && <p className="text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}
